/**
 * TagSelectorView — the tag picker opened from a pipeline Table row's `Add`
 * chip (SE-6554 restyle).
 *
 * ## What changed and why
 *
 * The dialog used to paint itself from the kanban dark-palette constant — a
 * raw palette, applied unconditionally. That made it a fixed navy slab with
 * fixed light labels no matter the active theme, so in light mode it was a
 * dark box floating over a white app. Every surface here is now a semantic
 * token, so both themes flip at the config level:
 *
 *   - `surface-base`   → background-0   255,255,255 light / 18,18,18 dark
 *   - `surface-raised` → background-50  246,246,246 light / 39,38,37 dark
 *   - `content-primary`→ typography-900  38,38,39  light / 245,245,245 dark
 *   - `content-muted`  → own var (SE-8550) 115,115,115 light / 163,163,163 dark
 *   - `outline-subtle` → outline-100    230,230,230 light / 65,65,65 dark
 *
 * The chrome follows the `NewPipelineModalView` / `RulesPanelView` family: the
 * default `ModalBackdrop`, a `ModalContent` with 24px padding, a `title-lg`
 * heading over a muted one-liner, and caption/uppercase section headers in
 * place of the old colon-suffixed `Current Tags:` / `Available Tags:` labels.
 *
 * ## Commit model
 *
 * Rows toggle a draft set; the count-reflective CTA applies it and is disabled
 * until something actually differs from what the player already carries. The
 * chips above it show that same draft, so removing a chip is an edit to the
 * pending selection rather than a separate immediate mutation — one commit
 * point, not two.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/organisms/TagSelector
 */

import { X } from "lucide-react-native";
import { memo } from "react";

import { type Tag } from "@/features/player-kanban/types/types";
import TagSelectorAvailableTags from "@/features/player-kanban/components/molecules/TagSelectorAvailableTags/TagSelectorAvailableTagsContainer";
import TagSelectorCurrentTags from "@/features/player-kanban/components/molecules/TagSelectorCurrentTags/TagSelectorCurrentTagsContainer";
import TagSelectorSearchSection from "@/features/player-kanban/components/molecules/TagSelectorSearchSection/TagSelectorSearchSectionContainer";

import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 *
 */
interface TagSelectorViewProps {
  isOpen: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isCreating: boolean;
  isApplying: boolean;
  hasChanges: boolean;
  selectedTags: (Tag & { color: string })[];
  filteredTags: (Tag & { color: string })[];
  selectedTagIds: readonly string[];
  canCreateNew: boolean;
  onToggleTag: (tag: Tag) => void;
  onCreateFromSearch: () => void;
  onApply: () => void;
  onManageTags: () => void;
}

/**
 * TagSelectorView - Main Modal Component
 * Pure UI component that orchestrates the tag selection modal using focused sub-components
 * @param root0 - The component props
 * @param root0.isOpen - Whether the modal is currently open
 * @param root0.onClose - Callback to close the modal
 * @param root0.searchQuery - Current search query for filtering tags
 * @param root0.onSearchQueryChange - Callback when search query changes
 * @param root0.isCreating - Whether a tag is currently being created
 * @param root0.isApplying - Whether the selection is being committed
 * @param root0.hasChanges - Whether the draft differs from the player's committed tags
 * @param root0.selectedTags - The draft selection, as full tags, for the chip row
 * @param root0.filteredTags - Tags filtered by search query
 * @param root0.selectedTagIds - IDs in the draft selection
 * @param root0.canCreateNew - Whether a new tag can be created with current search query
 * @param root0.onToggleTag - Callback to toggle a tag in the draft selection
 * @param root0.onCreateFromSearch - Callback to create tag from search query
 * @param root0.onApply - Callback to commit the draft selection
 * @param root0.onManageTags - Callback to open the tag library editor
 */
const TagSelectorView = ({
  isOpen,
  onClose,
  searchQuery,
  onSearchQueryChange,
  isCreating,
  isApplying,
  hasChanges,
  selectedTags,
  filteredTags,
  selectedTagIds,
  canCreateNew,
  onToggleTag,
  onCreateFromSearch,
  onApply,
  onManageTags,
}: TagSelectorViewProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size="md">
    <ModalBackdrop />
    {/* 24px on all four sides. `ModalContent` carries its own padding, so this
        overrides rather than nests — a second inset would stack. */}
    <ModalContent testID="tag-selector-modal" className="!p-6">
      <ModalBody>
        <Stack className="gap-4">
          <Stack
            direction="horizontal"
            className="items-start justify-between gap-3"
          >
            <Stack className="min-w-0 flex-1 gap-1">
              <Text variant="title-lg" className="text-content-primary">
                Tags
              </Text>
              <Text variant="body" className="text-content-muted">
                Search for a tag to add, or create a new one.
              </Text>
            </Stack>
            <ModalCloseButton>
              <Icon as={X} size="sm" className="text-content-muted" />
            </ModalCloseButton>
          </Stack>

          {/* Search/Filter Section */}
          <TagSelectorSearchSection
            searchQuery={searchQuery}
            onSearchQueryChange={onSearchQueryChange}
            canCreateNew={canCreateNew}
            onCreateFromSearch={onCreateFromSearch}
          />

          {/* Draft selection, as removable chips */}
          <TagSelectorCurrentTags
            currentPlayerTags={selectedTags}
            onToggleTag={onToggleTag}
          />

          {/* Available Tags */}
          <TagSelectorAvailableTags
            searchQuery={searchQuery}
            filteredTags={filteredTags}
            playerTagIds={selectedTagIds}
            isCreating={isCreating}
            canCreateNew={canCreateNew}
            onToggleTag={onToggleTag}
            onCreateFromSearch={onCreateFromSearch}
          />

          <Stack
            direction="horizontal"
            className="items-center justify-between gap-3 border-t border-t-outline-subtle pt-4"
          >
            {/* The picker only mounts for viewers who may edit tags, so the
                editor link needs no further gate. The PRD scopes it to
                managers and above; there is no role signal here beyond that
                edit right — flagged rather than approximated. */}
            <Pressable
              onPress={onManageTags}
              accessibilityRole="button"
              accessibilityLabel="Manage tags"
              testID="tag-selector-manage-tags"
            >
              <Text variant="caption" className="text-accent-primary">
                Manage tags
              </Text>
            </Pressable>
            <Button
              testID="tag-selector-apply"
              label={
                selectedTagIds.length === 1
                  ? "Apply 1 tag"
                  : `Apply ${selectedTagIds.length} tags`
              }
              variant="solid"
              tone="accent"
              size="md"
              isDisabled={!hasChanges || isApplying}
              isLoading={isApplying}
              onPress={onApply}
              accessibilityLabel="Apply tags"
            />
          </Stack>
        </Stack>
      </ModalBody>
    </ModalContent>
  </Modal>
);

TagSelectorView.displayName = "TagSelectorView";

export default memo(TagSelectorView);
